import { useCallback, useMemo, useState } from "react";
import { styled } from "@pigment-css/react";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { useApiKeys, useRevokeApiKey } from "@acto/taskpane/core/data-access/api-keys";
import { ManagementDataTable } from "../ui/DataTable/ManagementDataTable";
import { ManagementDataTableSkeleton } from "../ui/DataTable/ManagementDataTableSkeleton";
import type { Column, Row } from "../ui/DataTable/types";
import { PageHeader } from "../ui/PageHeader/PageHeader";
import { Flex } from "@acto/taskpane/core/components/flex/Flex";
import { GenerateApiKeyDialog } from "./api-keys/GenerateApiKeyDialog";
import { PaddingWrapper, Divider } from "../ui/layout";

const GenerateButton = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.managementView.spacing[8],
  height: "52px",
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[15],
  fontWeight: theme.managementView.font.weight[500],
  color: theme.managementView.colors.neutral[90],
  backgroundColor: theme.managementView.colors.neutral[8],
  border: "none",
  borderRadius: "50px",
  padding: `0 ${theme.managementView.spacing[24]}`,
  cursor: "pointer",
  transition: "background-color 0.2s",
  "&:hover": {
    backgroundColor: theme.managementView.colors.neutral[24],
  },
}));

const RevokeButton = styled("button")(({ theme }) => ({
  fontFamily: theme.managementView.font.family,
  fontSize: theme.managementView.font.size[13],
  fontWeight: theme.managementView.font.weight[500],
  color: theme.managementView.colors.neutral[56],
  backgroundColor: "transparent",
  border: `1px solid ${theme.managementView.colors.neutral[24]}`,
  borderRadius: theme.managementView.radius[10],
  padding: `${theme.managementView.spacing[4]} ${theme.managementView.spacing[12]}`,
  cursor: "pointer",
  transition: "background-color 0.2s",
  "&:hover": {
    backgroundColor: theme.managementView.colors.neutral[4],
  },
  "&:disabled": {
    opacity: 0.4,
    cursor: "not-allowed",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
}));

const RevokedBadge = styled("span")(({ theme }) => ({
  fontFamily: theme.managementView.font.family,
  fontSize: theme.managementView.font.size[13],
  fontWeight: theme.managementView.font.weight[500],
  color: theme.managementView.colors.neutral[56],
  fontStyle: "italic",
}));

const ScopeChip = styled("span")(({ theme }) => ({
  display: "inline-block",
  fontFamily: theme.managementView.font.family,
  fontSize: theme.managementView.font.size[13],
  color: theme.managementView.colors.neutral[56],
  backgroundColor: theme.managementView.colors.neutral[4],
  borderRadius: theme.managementView.radius[24],
  padding: `${theme.managementView.spacing[2]} ${theme.managementView.spacing[6]}`,
}));

const ScopeList = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.managementView.spacing[4],
}));

function formatDateTime(dateTime: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(dateTime));
}

export const ApiKeysPage = () => {
  const t = useTranslations("apiKeys");
  const { locale } = useLocale();
  const { apiKeys, isPending, refetch } = useApiKeys();
  const { revokeApiKey } = useRevokeApiKey();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const handleRevoke = useCallback(
    async (id: string) => {
      setRevokingId(id);
      await revokeApiKey(id);
      refetch();
      setRevokingId(null);
    },
    [revokeApiKey, refetch],
  );

  const apiKeysById = useMemo(() => new Map(apiKeys.map((key) => [key.id, key])), [apiKeys]);

  const columns: Column[] = [
    { key: "name", label: t("columns.name"), type: "string" },
    { key: "prefixHint", label: t("columns.keyPreview"), type: "string" },
    {
      key: "scopes",
      label: t("columns.scopes"),
      type: "custom",
      renderCell: (row: Row) => {
        const key = apiKeysById.get(row.id);
        return (
          <ScopeList>
            {(key?.scopes ?? []).map((scope) => (
              <ScopeChip key={scope}>{t(`scopes.${scope}`)}</ScopeChip>
            ))}
          </ScopeList>
        );
      },
    },
    { key: "createdBy", label: t("columns.createdBy"), type: "string" },
    { key: "createdAt", label: t("columns.created"), type: "string" },
    { key: "lastUsedAt", label: t("columns.lastUsed"), type: "string" },
    {
      key: "actions",
      label: t("columns.actions"),
      type: "custom",
      renderCell: (row: Row) => {
        const key = apiKeysById.get(row.id);
        if (key?.revokedAt) {
          return <RevokedBadge>{t("revoked")}</RevokedBadge>;
        }
        return (
          <RevokeButton onClick={() => handleRevoke(row.id)} disabled={revokingId === row.id}>
            {t("revoke")}
          </RevokeButton>
        );
      },
    },
  ];

  const rows: Row[] = apiKeys.map((key) => ({
    id: key.id,
    name: key.name,
    prefixHint: key.prefixHint,
    scopes: "",
    createdBy: key.createdBy ?? "",
    createdAt: formatDateTime(key.createdAt, locale),
    lastUsedAt: key.lastUsedAt ? formatDateTime(key.lastUsedAt, locale) : t("never"),
    actions: "",
  }));

  return (
    <>
      <PaddingWrapper>
        <PageHeader
          title={t("title")}
          subtitle={t("subtitle")}
          actionButton={<GenerateButton onClick={() => setIsDialogOpen(true)}>{t("generateKey")}</GenerateButton>}
        />
      </PaddingWrapper>

      <Flex direction="column" gap={6}>
        <PaddingWrapper direction="column" gap={6}>
          <Divider />
        </PaddingWrapper>
        {isPending ? (
          <ManagementDataTableSkeleton columns={columns} rowCount={5} />
        ) : (
          <ManagementDataTable columns={columns} rows={rows} />
        )}
      </Flex>

      <GenerateApiKeyDialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} onGenerated={refetch} />
    </>
  );
};
